import { isDefined } from "@clipboard-health/util-ts";
import {
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { type AxiosError, type CustomParamsSerializer } from "axios";
import type { z } from "zod";

import { get, type ZodSchema } from "./parsedApi";
import { type ReactQueryMeta } from "./types";

type LocallyDefinedUseInfiniteQueryOptions = "meta" | "queryKey" | "queryFn" | "getNextPageParam";
type DisallowedUseInfiniteQueryOptions = "onSuccess" | "onError" | "onSettled";

export type UseInfiniteGetParsedQueryOptions<
  ResponseSchema extends ZodSchema,
  SelectData = z.output<ResponseSchema>
> = Omit<
  UseInfiniteQueryOptions<
    z.output<ResponseSchema>,
    AxiosError,
    SelectData,
    z.output<ResponseSchema>,
    [string, unknown]
  >,
  LocallyDefinedUseInfiniteQueryOptions | DisallowedUseInfiniteQueryOptions
>;

interface BaseProps<ResponseSchema extends ZodSchema, PageParameter> {
  url: string;
  responseSchema: ResponseSchema;
  customHeaders?: Record<string, string>;
  paramsSerializer?: CustomParamsSerializer;
  meta: ReactQueryMeta;
  getNextPageParam: (
    lastPage: z.output<ResponseSchema>,
    allPages: Array<z.output<ResponseSchema>>
  ) => PageParameter | undefined;
}

type PropsWithQueryParams<
  ResponseSchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema,
  PageParameter,
  SelectData = z.output<ResponseSchema>
> = BaseProps<ResponseSchema, PageParameter> &
  UseInfiniteGetParsedQueryOptions<ResponseSchema, SelectData> & {
    queryParamsSchema: QueryParamsSchema;
    queryParams: z.input<QueryParamsSchema>;
  };

type PropsWithoutQueryParams<
  ResponseSchema extends ZodSchema,
  PageParameter,
  SelectData = z.output<ResponseSchema>
> = BaseProps<ResponseSchema, PageParameter> &
  UseInfiniteGetParsedQueryOptions<ResponseSchema, SelectData> & {
    queryParamsSchema?: undefined;
    queryParams?: undefined;
  };

export type UseInfiniteGetParsedQueryProps<
  ResponseSchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
  PageParameter = Record<string, unknown>,
  SelectData = z.output<ResponseSchema>
> = QueryParamsSchema extends ZodSchema
  ? PropsWithQueryParams<ResponseSchema, QueryParamsSchema, PageParameter, SelectData>
  : PropsWithoutQueryParams<ResponseSchema, PageParameter, SelectData>;

export function useInfiniteGetParsedQuery<
  ResponseSchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
  PageParameter = Record<string, unknown>,
  SelectData = z.output<ResponseSchema>
>(
  props: UseInfiniteGetParsedQueryProps<
    ResponseSchema,
    QueryParamsSchema,
    PageParameter,
    SelectData
  >
): UseInfiniteQueryResult<SelectData> {
  const {
    url,
    queryParams,
    queryParamsSchema,
    responseSchema,
    customHeaders,
    paramsSerializer,
    getNextPageParam,
    ...options
  } = props;

  return useInfiniteQuery({
    ...options,
    queryKey: [url, queryParams],
    queryFn: async ({ pageParam }) => {
      if (isDefined(queryParamsSchema)) {
        // Merge base queryParams with pageParam for pagination
        const mergedParams = isDefined(pageParam)
          ? {
              ...(queryParams as Record<string, unknown>),
              ...(pageParam as Record<string, unknown>),
            }
          : queryParams;

        const response = await get({
          url,
          queryParams: mergedParams,
          queryParamsSchema,
          responseSchema,
          customHeaders,
          paramsSerializer,
        });
        return response.data;
      }

      const response = await get({
        url,
        responseSchema,
        customHeaders,
        paramsSerializer,
      });
      return response.data;
    },
    getNextPageParam,
  });
}
